import {SidebarControlsConfiguration} from "../../../config/sidebar-configuration";
import EsriLegendViewModel from "@arcgis/core/widgets/Legend/LegendViewModel";
import {observer} from "mobx-react";
import React, {JSX, SyntheticEvent} from "react";
import {Dropdown, ListIcon, Tab, Table, TableBody, TableCell, TableRow} from "semantic-ui-react";
import Swipe from "@arcgis/core/widgets/Swipe";
import {RisikoanalyseStore} from "../../../stores/risikoanalyse-store-mobx";
import {DropdownProps} from "semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown";
import {withStore} from "../../../core/stores/abstract-app-store";
import {autorun} from "mobx";

import "./steckbrief-selector.scss";
import {Steckbrief} from "../../../models/steckbrief";
import {ProcessSource} from "../../../models/process-source";
import {Municipality} from "../../../models/municipality";


export interface SteckbriefSelectorProps {
    controlsConfig: SidebarControlsConfiguration;
    keyAppend?: string | number | undefined;
    onLoad?: (legendViewModel: EsriLegendViewModel) => any | undefined;
    small?: boolean;
    standalone?: boolean;
    store?: RisikoanalyseStore; // this is injected by withStore
}

@observer
class SteckbriefSelectorBase extends React.Component<SteckbriefSelectorProps, any> {
    cssClass: string = "geo-steckbrief-selector";
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    widget: Swipe;
    widgetKey = "steckbrief-selector";

    constructor(props: SteckbriefSelectorProps) {
        super(props);
        this.state = {
            // define options here to prevent errors
            mainProcessOptions: [
                { text: "Wasser", value: "hpa-1" },
                { text: "Oberflächenabfluss", value: "hpa-2" },
                { text: "Sturz", value: "hpa-3" },
                { text: "Rutschung", value: "hpa-4" },
                { text: "Lawinen", value: "hpa-5" }
            ],
            processSourceOptions: [],
            filteredOptions: [],
            municipalityOptions: [],
            mainProcessValue: undefined,
            processSourceValue: undefined,
            municipalityValue: undefined
        };

        this.handleTabChange = this.handleTabChange.bind(this);

        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
    }

    componentDidMount(): void {
        const {store} = this.props;
        if (!store) {
            return;
        }
        store.uiStore.addWidget(this.widgetKey, this);

        this.autorunDisposers.push(
            autorun(() => {
                if (!store.mapStore.view || !store.appStore.subtopic) {
                    return;
                }

                this.loadDropdownOptionsForProcessSource(store);
                this.loadMunicipalities(store);
            })
        );
    }

    // noinspection DuplicatedCode
    componentWillUnmount(): void {
        const {store} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
    }

    render() {
        const {store} = this.props;

        if (!store) {
            return null;
        }

        let steckbriefeList;
        if (store.SteckbriefStore.visibleItems.length > 0) {
            const tableRows = store.SteckbriefStore.visibleItems.map((steckbrief: Steckbrief) => {
                const path = `${store.mapViewerConfig.steckbriefe?.downloadUrl}/${steckbrief.path}`;
                if (path) {
                    return (
                        <TableRow key={steckbrief.fileName}>
                            <TableCell width="1"><ListIcon name="file pdf outline"/></TableCell>
                            <TableCell>
                                <a href={path} target="_blank" rel="noreferrer">{steckbrief.label}</a>
                            </TableCell>
                            <TableCell>
                                {steckbrief.riskType}
                            </TableCell>
                            <TableCell width="3" textAlign="right">{steckbrief.fileSizeLabel}</TableCell>
                        </TableRow>
                    );
                }
            });
            steckbriefeList = (
                <Table basic='very'>
                    <TableBody>
                        {tableRows}
                    </TableBody>
                </Table>
            );
        }

        const processSourcePane = this.initProcessSourcePane(steckbriefeList);
        const municipalityPane: JSX.Element = this.initMunicipalityPane(steckbriefeList);
        const panes = [
            {
                menuItem: "Prozessquellen",
                pane: {
                    key: "processSourcePane",
                    content: processSourcePane
                }
            },
            {
                menuItem: "Gemeinde",
                pane: {
                    key: "municipalityPane",
                    content: municipalityPane
                }
            }
        ];

        return (
            <Tab panes={panes}
                renderActiveOnly={false}
                onTabChange={this.handleTabChange} />
        );
    }

    private initProcessSourcePane(steckbriefe: JSX.Element | undefined): JSX.Element {
        const {mainProcessOptions, mainProcessValue, filteredOptions, processSourceValue} = this.state;
        const {store} = this.props;
        if (!store) {
            return (<></>);
        }

        const mainProcessDropdown: JSX.Element = (
            <Dropdown
                placeholder="Hauptprozessart"
                fluid
                selection
                closeOnChange
                value={mainProcessValue}
                options={mainProcessOptions}
                onChange={(event: SyntheticEvent, data: DropdownProps) => {
                    // TODO: ev. reset SteckbriefStore items
                    this.setState({
                        mainProcessValue: data.value
                    });
                    this.updateProcessSourceDropdown(data.value as string);
                }} />
        );

        const processSourceDropdown = (
            <Dropdown
                placeholder="Prozessquelle"
                fluid
                selection
                closeOnChange={true}
                openOnFocus={false}
                search
                value={processSourceValue}
                options={filteredOptions}
                onChange={(event: SyntheticEvent, data: DropdownProps) => {
                    this.setState({
                        processSourceValue: data.value
                    });
                    store?.SteckbriefStore.loadItems(undefined, [data.value as string]);
                }}/>
        );

        return (
            <div className={this.cssClass}>
                <div>
                    {mainProcessDropdown}
                    {filteredOptions ? processSourceDropdown : null}
                    {steckbriefe}
                </div>
            </div>
        );
    }

    private loadDropdownOptionsForProcessSource(store: RisikoanalyseStore): any[] {
        // load dropdown options
        store.ProcessSourcesStore?.loadItems().then((items: ProcessSource[]) => {
            const options = items.sort(ProcessSource.sortByName).map((processSource: any) => {
                return {
                    key: processSource.guid,
                    text: processSource.displayLabel,
                    value: processSource.processSourceNr,
                    hpa: processSource.mainProcessId
                };
            });
            this.setState({processSourceOptions: options});
            this.setState({filteredOptions: options});
            return options;
        });
        return [];
    }

    private loadMunicipalities(store: RisikoanalyseStore): void {
        store.MunicipalityStore?.loadItems(undefined).then((items: any) => {
            const options = items.sort(Municipality.sortByName).map((feature: any) => {
                return {
                    key: feature.guid,
                    text: feature.displayLabel,
                    value: feature.guid,
                    hpa: feature.mainProcessId
                };
            });
            this.setState({municipalityOptions: options});
        });
    }

    private updateProcessSourceDropdown(hpa: string | undefined) {
        const {processSourceOptions} = this.state;
        if (!hpa) {
            this.setState({
                filteredOptions: processSourceOptions
            });
            return;
        }
        const mainProcessId = parseFloat(hpa.substring(hpa.indexOf("-")+1, hpa.length));

        const filteredValues = processSourceOptions.filter((processSource: any) => {
            return processSource.hpa === mainProcessId as number;
        });
        this.setState({
            filteredOptions: filteredValues
        });
    }

    private handleTabChange(): void {
        const {store} = this.props;
        this.updateProcessSourceDropdown(undefined);
        this.setState({
            mainProcessValue: "",
            processSourceValue: "",
            municipalityValue: ""
        });
        store?.SteckbriefStore.resetVisibleItems();
    }

    private initMunicipalityPane(steckbriefe: JSX.Element | undefined): JSX.Element {
        const {store} = this.props;
        const {municipalityOptions, municipalityValue} = this.state;

        const municipalityDropdown = (
            <Dropdown
                placeholder="Gemeinde"
                fluid
                selection
                closeOnChange={true}
                openOnFocus={false}
                search
                value={municipalityValue}
                options={municipalityOptions}
                onChange={(event: SyntheticEvent, data: DropdownProps) => {
                    this.setState({
                        municipalityValue: data.value
                    });
                    store?.SteckbriefStore.loadItems([data.value as string]);
                }}/>
        );

        return (
            <div className={this.cssClass}>
                <div>
                    {municipalityDropdown}
                    {steckbriefe}
                </div>
            </div>
        );
    }
}

export const SteckbriefSelector = withStore(SteckbriefSelectorBase);
